import React, { useEffect } from 'react';

const SRC = 'https://widgets.smartcustomer.com/js/v2/widgets.js';
const WIDGET_ID = '5f76395cc3996';

type Props = {
  children?: React.ReactNode;
};

/**
 * Injects the SmartCustomer widget loader imperatively.
 *
 * One loader serves every company-wide widget: the `data-id` is our SmartCustomer
 * account, and which widget renders is decided by the container class already in the
 * page — `.stjr-review-carousel` on the homepage, `.stjr-review-showcase` on
 * /guarantee. So a page needs this component and its own mount point, nothing more.
 *
 * This cannot be a `<script>` in JSX. React DOM creates script elements through a
 * detached div's innerHTML precisely so their "parser-inserted" flag is set and they
 * never execute — the tag lands in the DOM looking correct and is silently never
 * fetched. headless_jshop gets away with the JSX form only because Next.js server
 * renders it, so the HTML parser creates the element rather than React.
 *
 * The effect runs after children are committed, so the mount point already exists by
 * the time the widget script looks for it. A page that gates the loader on scroll
 * instead — /guarantee does — must render its container unconditionally for the same
 * reason, and mount this only once the container is in the DOM.
 *
 * The guard makes a second instance a no-op, so a page may carry more than one.
 */
export default function CompanyReviewScript({ children }: Props) {
  useEffect(() => {
    if (document.querySelector(`script[src="${SRC}"]`)) {
      return;
    }

    const script = document.createElement('script');
    script.src = SRC;
    script.dataset.id = WIDGET_ID;
    script.async = true;

    document.body.appendChild(script);
  }, []);

  return <>{children}</>;
}
